import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useContext, useMemo } from 'react';

import { Pic } from '@webapp/common/assets/icons';
import { IconFullscreen } from '@webapp/common/assets/icons/fullscreen';
import { Checkbox } from '@webapp/common/components/checkbox';
import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { CssPageBlock, CssUiComponent } from '@webapp/common/components/survey-custom';
import { CDN_URL } from '@webapp/common/conf';
import { setHtml } from '@webapp/common/lib/ui';
import { striptags } from '@webapp/common/lib/utils';
import { ImageTextPosition } from '@webapp/common/resources/entities';
import { QuestionType } from '@webapp/common/resources/survey';

import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';

import css from './select-image.css';

const Label: FC<{
    text: string;
}> = ({ text }) => {
    const { text: textStyle } = useContext(CustomStylesCtx);
    return <div className={cn(CssUiComponent.LABEL, css.text)} style={textStyle} {...setHtml(text)} />;
};

export const Image: VFC<{
    index: number;
    gap: string;
    textPosition: ImageTextPosition;
    answer: QuestionAnswerActor;
    onClick: (e) => void;
    type: any;
    selectImage: { resizer?: CSSProperties };
    onClickZoom: (index: number) => (event: ReactMouseEvent<SVGSVGElement | HTMLDivElement, MouseEvent>) => void;
    aspect?: number;
    disabled?: boolean;
}> = ({ answer, aspect, gap, index, onClick, onClickZoom, selectImage: { resizer }, textPosition, type, disabled }) => {
    const pipingAnswersReplacer = usePipingAnswersReplacer();
    const {
        file,
        response: responseRaw,
        value: [text],
    } = useSelector(answer, (a) => a.context);
    const response = useSelector(responseRaw, (r) => r.context);
    const multiple = type === QuestionType.SELECT_ONE_IMAGE;
    const checked = Boolean(response.value);
    const zoom = useMemo(() => onClickZoom(index), [index, onClickZoom]);

    const imgFrameStyle: CSSProperties = useMemo(
        () => ({
            minWidth: !file ? 200 : undefined,
            '--border-color': resizer.color,
        }),
        [file, resizer.color],
    );

    const imgStyle: CSSProperties = useMemo(
        () => ({
            aspectRatio: `${aspect}` || 'auto',
        }),
        [aspect],
    );

    const style = useMemo(() => ({ gap }), [gap]);
    const isDisabled = !checked && disabled;

    const image = file ? (
        <img
            alt={text ? striptags(pipingAnswersReplacer(text)) : 'image'}
            height={file.height}
            loading='lazy'
            src={CDN_URL + file.path}
            style={imgStyle}
            width={file.width}
        />
    ) : (
        <Pic style={imgStyle} />
    );
    const { textField } = useContext(CustomStylesCtx);

    return (
        <div className={cn(CssPageBlock.ANSWER, css.answer)} style={style}>
            {textPosition === ImageTextPosition.LEFT ? (
                <div
                    className={cn(css.contentWrap, 'imageContentWrap', { imageContentWrapSelected: checked })}
                    onClick={isDisabled ? undefined : onClick}
                    style={{
                        boxShadow: checked ? `0 0 0 1px ${resizer.color}` : undefined,
                        backgroundColor: textField.backgroundColor,
                        borderColor: textField.borderColor,
                    }}
                >
                    <Label text={pipingAnswersReplacer(text)} />
                    <div
                        className={cn(css.image, !file && css.empty, 'imageWrap')}
                        style={imgFrameStyle}
                        onClick={zoom}
                    >
                        {image}
                        <IconFullscreen className={cn(css.zoomIcon, 'zoomIcon')} />
                    </div>
                </div>
            ) : (
                <>
                    {textPosition === ImageTextPosition.ABOVE && text && <Label text={pipingAnswersReplacer(text)} />}
                    <div
                        className={cn(css.image, !file && css.empty)}
                        style={imgFrameStyle}
                        onClick={isDisabled ? undefined : onClick}
                    >
                        {image}
                        <div className={css.checkbox}>
                            <Checkbox checked={checked} round={multiple} disabled={isDisabled} />
                        </div>
                        <IconFullscreen className={css.zoomIcon} onClick={zoom} />
                    </div>
                    {textPosition === ImageTextPosition.UNDER && text && <Label text={pipingAnswersReplacer(text)} />}
                </>
            )}
        </div>
    );
};
Image.displayName = 'Image';
